import React, { FunctionComponent, ReactNode, useCallback } from 'react';

import { Button } from '@yandex-360/orb-components/dist/components/Button';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import SvgImage from '../../../../shared/components/svg-image';
import { isMessengerIntegrationTier, selectIsTeleMessenger } from '../../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../../shared/features/parent-frame-communicator';
import { i18n } from '../../../../shared/helpers/i18n';
import { useBooleanStateAutoResetByTimer } from '../../../../shared/hooks/useBooleanStateAutoResetByTimer';
import { useTypedSelector } from '../../../../shared/hooks/useTypedSelector';
import { useShareCallData } from '../../hooks/share-call-data.hook';
import { OutgoingEvents } from '../../iframe-sagas/types';
import { selectIsConferencePhoneAvail } from '../../selectors';
import ShareButtons from '../share-buttons';
import styles from './styles.css';

const COPIED_RESET_TIMEOUT = 3000;

interface InviteOrCopyFooterProps {
    className?: string;
    onAddUsers?: () => void;
    addonRight?: ReactNode;
    overlay?: boolean;
}

const InviteOrCopyFooter: FunctionComponent<InviteOrCopyFooterProps> = ({
    className,
    onAddUsers,
    addonRight,
    overlay = false,
}) => {
    const { copyConferenceUrlToClipboard, copyInvitationToClipboard } = useShareCallData();

    const [isCopyConferenceUrlButtonHidden, setIsCopyConferenceUrlButtonHidden] =
        useBooleanStateAutoResetByTimer(COPIED_RESET_TIMEOUT);

    const isTeleMessenger = useSelector(selectIsTeleMessenger);
    const isMessengerIntegrationTier1 = useTypedSelector((state) => isMessengerIntegrationTier(state, 1));

    const onCopyLinkButtonClick = useCallback(() => {
        if (isMessengerIntegrationTier1 || isTeleMessenger) {
            parentFrameCommunicator.sendMessage('messenger', { event: OutgoingEvents.ADD_USERS_EVENT });
            onAddUsers?.();

            return;
        }

        copyConferenceUrlToClipboard();
        // пока не понятно останутся ли кнопки шаринга в мессенджерах или нет
        // пока не стал выпиливать этот функционал
        // setIsCopyConferenceUrlButtonHidden(true);
    }, [isMessengerIntegrationTier1, isTeleMessenger, copyConferenceUrlToClipboard, onAddUsers]);

    const hideButtons = useCallback(
        () => setIsCopyConferenceUrlButtonHidden(false),
        [setIsCopyConferenceUrlButtonHidden]
    );

    const isConferencePhoneAvail = useSelector(selectIsConferencePhoneAvail);
    const shouldShowInviteButton = isMessengerIntegrationTier1 || isTeleMessenger || !isConferencePhoneAvail;

    const onCopyInvitationButtonClick = useCallback(() => {
        copyInvitationToClipboard();
        // пока не понятно останутся ли кнопки шаринга в мессенджерах или нет
        // пока не стал выпиливать этот функционал
        // setIsCopyConferenceUrlButtonHidden(true);
    }, [copyInvitationToClipboard]);

    return (
        <div
            className={cn(styles.inviteOrCopyFooter, className, {
                [styles.withLinksMenu]: isCopyConferenceUrlButtonHidden,
                [styles.withOverlay]: overlay,
            })}
        >
            <div className={cn(styles.shareButtons)}>
                <div className={styles.copySuccessText}>
                    <SvgImage className={styles.okIcon} imageName="checkmark"></SvgImage>
                    {i18n('call', 'link-successfully-copied')}
                </div>
                <ShareButtons hideButtons={hideButtons} />
            </div>
            {shouldShowInviteButton ? (
                <Button view="neutral" className={styles.copyButton} onClick={onCopyLinkButtonClick}>
                    {isMessengerIntegrationTier1 || isTeleMessenger
                        ? i18n('call', 'invite-participants')
                        : i18n('common', 'copy-link-action')}
                </Button>
            ) : (
                <Button view="neutral" className={styles.copyButton} onClick={onCopyInvitationButtonClick}>
                    {i18n('call', 'copy-invitation')}
                </Button>
            )}
            {addonRight && !isCopyConferenceUrlButtonHidden ? (
                <div className={styles.AddonRight}>{addonRight}</div>
            ) : null}
        </div>
    );
};

export { InviteOrCopyFooter };
